import type { IRogue, BuyRequest } from "@/js/services/RogueTypes"
import { useProductStore } from "@/js/stores/productStore"
import { SLINGER_X_SKUS, SLINGER_X_BUNDLE_SKUS, SLINGER_X_SIDE_SKUS } from "./slingerXConstants"

export function isSlingerX(rogue: IRogue, item: BuyRequest) {
  const product = rogue.getProductById(item.productId)

  return SLINGER_X_SKUS.includes(product?.sku)
}

// A LEFT/RIGHT side bundle whose attachments are all set to "None" is an empty container - it carries no
// price, no weight and nothing to pick - so it must not become its own cart/order line. Side selection is
// expressed by the qty on these bundles, which is why they otherwise survive the qty > 0 filter.
export function isEmptySlingerXSideBundle(rogue: IRogue, item: BuyRequest): boolean {
  const sku = rogue.getProductById(item.productId)?.sku

  return SLINGER_X_SIDE_SKUS.includes(sku) && !item.children?.some(child => child.qty > 0)
}

// Fallback for pre-recipe orders — the first 8 hex chars of the Mongo ObjectId _id/listItemId is a 1-second-resolution creation timestamp, which can collide across rapid back-to-back purchases (unlike recipe).
function legacyPurchaseTimestamp(item: BuyRequest & { [key: string]: any }): string {
  const id = item._id || item.listItemId || ''

  return typeof id === 'string' && id.length >= 8 ? id.slice(0, 8) : 'none'
}

// metadata.recipe alone identifies a purchase — sibling bundles in the same purchase can carry different groupedProductId values, so that can't be required to match too.
export function getSlingerXPurchaseKey(item: BuyRequest & { [key: string]: any }): string {
  return item.metadata?.recipe ?? `${item.metadata?.groupedProductId ?? 'none'}:${legacyPurchaseTimestamp(item)}`
}

export function isSameSlingerXPurchase(a: BuyRequest, b: BuyRequest): boolean {
  return getSlingerXPurchaseKey(a) === getSlingerXPurchaseKey(b)
}

// MARK: RSS
// A side/accessory item ships alongside its lead purchase but isn't a separate physical unit — resolved via the live catalog product (item.sku itself can be a stale/dynamic value stamped at purchase time), purchase key covers cases where even the catalog sku isn't a recognized side constant.
export function isSlingerXSideItem(rogue: IRogue, item: BuyRequest & { [key: string]: any }, leadPurchaseKeys: Set<string>): boolean {
  const catalogSku = rogue.getProductById(item.productId)?.sku
  
  return SLINGER_X_SIDE_SKUS.includes(catalogSku) || leadPurchaseKeys.has(getSlingerXPurchaseKey(item))
}

function isSlingerXLead(rogue: IRogue, item: BuyRequest & { [key: string]: any }): boolean {
  return SLINGER_X_BUNDLE_SKUS.includes(rogue.getProductById(item.productId)?.sku)
}

// Collapses repeat purchases of the same sku (e.g. buying two racks in one order) into one row with a summed quantity.
export function collapseSlingerXLeadItems(rogue: IRogue, items: (BuyRequest & { [key: string]: any })[]): (BuyRequest & { [key: string]: any })[] {
  const grouped = new Map<string, BuyRequest & { [key: string]: any }>()

  for (const item of items) {
    const name = item.productId ? rogue.getProductById(item.productId)?.name || item.name : item.name
    const key = item.sku ?? ''
    const quantity = item.quantity ?? item.qty ?? 0
    const existing = grouped.get(key)

    if (existing) {
      existing.quantity = (existing.quantity || 0) + quantity
    } else {
      grouped.set(key, { ...item, name, quantity })
    }
  }

  return [...grouped.values()]
}

// Slinger X ships as sibling bundle items per unit purchased — only the lead rack is a real physical unit, so side items are dropped and repeat lead purchases are collapsed into one row.
export function resolveSlingerXVisibleItems(rogue: IRogue, items: (BuyRequest & { [key: string]: any })[]): (BuyRequest & { [key: string]: any })[] {
  function isLead(item: BuyRequest & { [key: string]: any }): boolean {
    return isSlingerXLead(rogue, item)
  }

  const leadItems = items.filter(isLead)
  const leadPurchaseKeys = new Set(leadItems.map(getSlingerXPurchaseKey))

  function isVisibleNonLeadItem(item: BuyRequest & { [key: string]: any }): boolean {
    return !isLead(item) && !isSlingerXSideItem(rogue, item, leadPurchaseKeys)
  }

  const otherItems = items.filter(isVisibleNonLeadItem)

  return [...otherItems, ...collapseSlingerXLeadItems(rogue, leadItems)]
}

// 'Functional' (not the full title) is intentional — some SKUs typo the option title "Functional Tainer:".
const HIDDEN_SLINGER_X_BUNDLE_OPTION_PREFIXES = ['Rack', 'Functional']

// True for a bundle line's Rack:/Functional Trainer: config-carrier child — the rows every Slinger X surface (cart, order history, PDF, email) summarises instead of listing.
export function isSlingerXConfigCarrierChild(rogue: IRogue, bundle: BuyRequest, child: BuyRequest): boolean {
  const bundleOptions = rogue.getProductById(bundle.productId)?.bundleOptions

  if (!bundleOptions) return false

  const option = bundleOptions.find(opt => opt.id === child.metadata?.bundleOptionId)

  // Runs inside the checkout hook, so a non-string catalog title must not throw — same coercion as isHybridRackLevelOption.
  const title = String(option?.title ?? '')

  return HIDDEN_SLINGER_X_BUNDLE_OPTION_PREFIXES.some(prefix => title.startsWith(prefix))
}

// Excludes the Rack:/Functional Trainer: config-carrier children, keeping only real accessories.
export function getSlingerXAccessoryChildren(rogue: IRogue, buyRequest: BuyRequest): BuyRequest[] {
  return (buyRequest.children || [])
    .filter(child => !isSlingerXConfigCarrierChild(rogue, buyRequest, child))
    // Order placement overwrites children[0].name with the parent bundle's own name — restore the real product name, same fix as groupSlingerXItems below.
    .map(child => ({ ...child, name: rogue.getProductById(child.productId)?.name || child.name }))
}

// MARK: PDF
// Creates a fake flat Slinger X parent (no Rack/Left/Right sections), grouped by metadata.groupedProductId.
export function groupSlingerXItems(rogue: IRogue, orderCopy: BuyRequest & { [key: string]: any }) {
  const slingerXItems: (BuyRequest & { [key: string]: any })[] = []
  const nonSlingerXItems: BuyRequest[] = []

  for (const item of orderCopy.items) {
    if (isSlingerX(rogue, item)) {
      slingerXItems.push(item)
    } else {
      nonSlingerXItems.push(item)
    }
  }

  if (slingerXItems.length === 0) return

  const productStore = useProductStore(rogue.pinia)

  // Each Slinger X purchase shares one groupedProductId + recipe across its mainRack/leftSide/rightSide items.
  const groups: Record<string, (BuyRequest & { [key: string]: any })[]> = {}

  for (const item of slingerXItems) {
    const key = getSlingerXPurchaseKey(item)
    if (!groups[key]) groups[key] = []
    groups[key].push(item)
  }

  const slingerXParents = Object.values(groups).map(groupItems => {
    // Not just groupItems[0] — sibling order in the order/cart isn't guaranteed.
    const mainItem = groupItems.find(item => {
      const sku = item.sku || rogue.getProductById(item.productId)?.sku

      return SLINGER_X_BUNDLE_SKUS.includes(sku)
    }) || groupItems[0]

    let totalWeight   = 0
    let totalSubtotal = 0

    for (const bundle of groupItems) {
      const bundleChildren = bundle.children || []
      for (const child of bundleChildren) {
        totalSubtotal += child.price || 0
        totalWeight += productStore.products?.[child.productId]?.weight || 0
      }
    }

    // Name/sku come from the shared group product, not mainItem's own — mainItem is an arbitrary pick when the group has no plain bundle sku (e.g. a dual left+right purchase).
    const groupProduct = rogue.getProductById(mainItem.metadata?.groupedProductId ?? mainItem.productId)

    return {
      ...mainItem,
      productId        : mainItem.productId,
      name             : groupProduct?.name,
      sku              : groupProduct?.sku,
      // Real subtotal/weight (unlike Cave, which blanks them) — read directly, not recomputed.
      slingerXSubtotal : totalSubtotal,
      slingerXWeight   : totalWeight,
      // bundleOptionId is stripped — these came from sibling bundles, not this fake parent, and would otherwise throw "Invalid bundle child" when getBuyRequestMeta recurses into item.children[0].
      children         : groupItems.flatMap(item => getSlingerXAccessoryChildren(rogue, item)).map(child => ({
        ...child,
        name     : rogue.getProductById(child.productId)?.name,
        metadata : { ...child.metadata, bundleOptionId: undefined },
      })),
      isSlingerXParent: true,
    }
  })

  orderCopy.items = [...slingerXParents, ...nonSlingerXItems]
}

// Helper function to identify the fake parent made for the PDF
export function isSlingerXParent(item: BuyRequest & { [key: string]: any }): boolean {
  return item.isSlingerXParent === true
}
